/**
 * Bucket challenges into home screen sections.
 * Each challenge is assigned to at most one bucket (priority: waitingForTaker > waitingForFriends >
 * yourMove > live > waitingForOpponent > active). waitingForTaker = your own open-board challenge
 * that no one has taken yet (specs/open-challenges); once taken it flows through the normal buckets.
 * Challenges move to live once the first game has started (regardless of submission status).
 */

import { MONKEY_USER_ID } from '../../../constants';
import type { ChallengeWithStats } from '../../../types/challenges';
import type { ChallengeSectionBuckets } from '../types/challenges.types';
import { isGuestPendingMonkeyArenaChallengeId } from '../constants/guestPendingArena.constants';

/**
 * Whether the challenge belongs in "Waiting for Friends" (creator, and at least one other participant is PENDING).
 */
function isWaitingForFriends(challenge: ChallengeWithStats, currentUserId: string): boolean {
  if (challenge.creatorId !== currentUserId) return false;
  return (
    challenge.participantStatuses?.some(
      (p) => p.userId !== currentUserId && p.status === 'PENDING'
    ) ?? false
  );
}

/**
 * Whether the challenge belongs in "Your Move":
 * - PENDING: you need to accept the invitation
 * - ACCEPTED + you have not submitted for all matches: you need to make/update picks
 */
function isYourMove(challenge: ChallengeWithStats, currentUserId: string): boolean {
  const currentStatus = challenge.participantStatuses?.find((p) => p.userId === currentUserId)
    ?.status;
  if (currentStatus === 'DECLINED') return false;
  if (currentStatus === 'PENDING') return true;
  return !currentUserHasSubmittedAll(challenge, currentUserId);
}

/**
 * Whether the user is a participant (creator or accepted).
 * Falls back to participantIds so old challenges without participantStatuses still show.
 */
function isParticipant(challenge: ChallengeWithStats, currentUserId: string): boolean {
  if (challenge.creatorId === currentUserId) return true;
  const status = challenge.participantStatuses?.find((p) => p.userId === currentUserId)?.status;
  if (status === 'ACCEPTED') return true;
  // Old challenges may not have participantStatuses; if user is in participantIds, show in Active
  return (challenge.participantIds?.includes(currentUserId)) ?? false;
}

const matchIds = (c: ChallengeWithStats): string[] => c.matchIds ?? [];

/** All picks (1X2 or other markets) for a user. */
const userPicksByMatch = (c: ChallengeWithStats, userId: string) =>
  (c.picks ?? [])
    .filter((p: { userId: string }) => p.userId === userId)
    .map((p: { matchId: string }) => p.matchId);

/** Current user has at least one pick (any market) for every match. */
function currentUserHasSubmittedAll(challenge: ChallengeWithStats, currentUserId: string): boolean {
  const matchIdList = matchIds(challenge);
  if (matchIdList.length === 0) return true;
  const myMatchIds = new Set(userPicksByMatch(challenge, currentUserId));
  return matchIdList.every((mid) => myMatchIds.has(mid));
}

/** Other participant has at least one pick (any market) for every match. Monkey auto-submits 1X2 on challenge creation. */
function opponentHasSubmittedAll(challenge: ChallengeWithStats, currentUserId: string): boolean {
  const matchIdList = matchIds(challenge);
  if (matchIdList.length === 0) return true;
  const otherUserId = challenge.participantIds?.find(
    (id) => id !== currentUserId && typeof id === 'string'
  );
  if (!otherUserId) return false;
  if (otherUserId === MONKEY_USER_ID) return true;
  const otherMatchIds = new Set(userPicksByMatch(challenge, otherUserId));
  return matchIdList.every((mid) => otherMatchIds.has(mid));
}

/** Earliest kickoff from picks' embedded `match.date` (API), when dateRange is absent or stale. */
function earliestKickoffFromPicks(challenge: ChallengeWithStats): number | null {
  const picks = challenge.picks;
  if (!Array.isArray(picks) || picks.length === 0) return null;
  let min = Infinity;
  for (const p of picks) {
    const m = (p as { match?: { date?: string | Date } }).match;
    const d = m?.date;
    if (d == null) continue;
    const t = new Date(d as string | Date).getTime();
    if (Number.isFinite(t) && t < min) min = t;
  }
  return min === Infinity ? null : min;
}

/**
 * True when the first game of the challenge has already started.
 * Uses dateRange.start when set; otherwise earliest embedded match date from picks (aligns with ChallengeCard countdown / "Started").
 */
function firstGameHasStarted(challenge: ChallengeWithStats): boolean {
  const start = challenge.dateRange?.start;
  if (start != null && new Date(start).getTime() <= Date.now()) return true;
  const earliest = earliestKickoffFromPicks(challenge);
  return earliest != null && earliest <= Date.now();
}

/** True when this is a 1v1 (friends) challenge, not a group challenge. */
function is1v1Challenge(challenge: ChallengeWithStats): boolean {
  return challenge.groupId == null || challenge.groupId === undefined;
}

/**
 * Whether this is the current user's OWN open-board challenge that no one has taken yet
 * (posted to the public board, still awaiting a taker). See specs/open-challenges AC-14.
 */
function isMyUntakenOpen(challenge: ChallengeWithStats, currentUserId: string): boolean {
  return (
    challenge.isOpen === true &&
    challenge.creatorId === currentUserId &&
    (challenge.takenAt == null)
  );
}

/**
 * Bucket challenges into home sections.
 * "Live" = first game has started. "Waiting for opponent" = you've submitted, opponent hasn't, game not started yet.
 * "Active" = both predicted (or other), game not started. "Finished" = COMPLETED challenges.
 */
export function bucketChallengesBySection(
  challenges: ChallengeWithStats[],
  currentUserId: string
): ChallengeSectionBuckets {
  const yourMove: ChallengeWithStats[] = [];
  const live: ChallengeWithStats[] = [];
  const waitingForFriends: ChallengeWithStats[] = [];
  const waitingForTaker: ChallengeWithStats[] = [];
  const waitingForOpponent: ChallengeWithStats[] = [];
  const active: ChallengeWithStats[] = [];
  const finished: ChallengeWithStats[] = [];

  for (const challenge of challenges) {
    if (isGuestPendingMonkeyArenaChallengeId(challenge.id)) {
      waitingForFriends.push(challenge);
      continue;
    }
    if (challenge.status === 'cancelled') continue;
    if (challenge.status === 'completed') {
      if (isParticipant(challenge, currentUserId)) {
        finished.push(challenge);
      }
      continue;
    }

    // Your own open-board challenge, still awaiting a taker (AC-14). Once taken (takenAt set) it
    // falls through to the normal active/live buckets like any other challenge (AC-15).
    if (isMyUntakenOpen(challenge, currentUserId)) {
      // Past first kickoff → hide (the expiry sweep will cancel + refund it).
      if (firstGameHasStarted(challenge)) continue;
      waitingForTaker.push(challenge);
      continue;
    }

    if (isWaitingForFriends(challenge, currentUserId)) {
      // 1v1 only: hide when first game already started (cron will cancel PENDING)
      if (is1v1Challenge(challenge) && firstGameHasStarted(challenge)) {
        continue;
      }
      // Group: first fixture underway → active (no longer "waiting for friends" bucket; remind hidden via section)
      if (!is1v1Challenge(challenge) && firstGameHasStarted(challenge)) {
        if (isParticipant(challenge, currentUserId)) {
          live.push(challenge);
        }
        continue;
      }
      waitingForFriends.push(challenge);
      continue;
    }
    if (isYourMove(challenge, currentUserId)) {
      // Hide when first game already started and user has not submitted all picks (1v1: cron will cancel; group: just hide, no cancel)
      if (
        firstGameHasStarted(challenge) &&
        !currentUserHasSubmittedAll(challenge, currentUserId)
      ) {
        continue;
      }
      yourMove.push(challenge);
      continue;
    }
    if (isParticipant(challenge, currentUserId)) {
      if (firstGameHasStarted(challenge)) {
        live.push(challenge);
      } else {
        const iveSubmitted = currentUserHasSubmittedAll(challenge, currentUserId);
        const opponentSubmitted = opponentHasSubmittedAll(challenge, currentUserId);
        if (iveSubmitted && !opponentSubmitted && matchIds(challenge).length > 0) {
          waitingForOpponent.push(challenge);
        } else {
          active.push(challenge);
        }
      }
    }
  }

  return {
    yourMove, live, waitingForFriends, waitingForTaker, waitingForOpponent, active, finished,
  };
}
